import { FC, useContext, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";

import EnrollmentCard from "../../components/enrollmentCard";
import FooterText from "../../components/footerText";
import NPIInputField from "../../components/inputType";
import EnrollmentPracticeForm from "../../components/enrollmentComp/practiceForm";
import EnrollmentUserForm from "../../components/enrollmentComp/userForm";
import EnrollmentPrescriberForm from "../../components/enrollmentComp/prescriberForm";

import useFetch from "src/hooks/useFetch";
import { ToastContext } from "src/context/toastContext";

import {
  AddEnrollmentErrorType,
  AddEnrollmentResponseType,
  EnrollmentPrescriber,
  EnrollmentUser,
  FieldName,
  NewEnrollmentType,
  NewEnrollmentTypeForAPI,
} from "./types";
import { APIEndpoints } from "src/types/apiTypes";
import { NPILookupResponse, ToastTypes } from "src/types";
import { NPISpeciality } from "../addProvider/types";
import { yupResolver } from "@hookform/resolvers/yup";
import { NewEnrollmentSchema } from "src/validation/newEnrollment";

import { LoaderContext } from "src/context/loaderContext";
import { populateEnrollmentForm } from "src/helpers/enrollment";

const NewEnrollment: FC = (): JSX.Element => {
  const [preservedNpi, updatePreservedNpi] = useState<string>("");
  const formData = new FormData();
  const [isModalVisible, setIsModalVisible] = useState(false);

  const { showToast } = useContext(ToastContext);
  const { setLoader, hideLoader } = useContext(LoaderContext);
  const {
    control,
    formState,
    handleSubmit,
    getValues,
    setValue,
    watch,
    reset,
    trigger,
    resetField,
  } = useForm<NewEnrollmentType>({
    mode: "all",
    resolver: yupResolver(NewEnrollmentSchema),
    defaultValues: {
      enrollment_contacts_attributes: [{} as EnrollmentUser],
      enrollment_doctors_attributes: [{} as EnrollmentPrescriber],
    },
  });

  // useLayoutEffect(() => setLoader(), [])

  /**
   * Fetching doctor specialities to be rendered in prescriber's seciton
   * @First useFetch API integration
   * @Second useEffect to handle api response
   */
  const { data: specialityData } = useFetch<NPISpeciality[]>(
    APIEndpoints.GET_PROVIDER_SPECIALITIES,
  );

  /**
   * @NPILookup calling npi lookup for details
   * @first useFetch hook for api call
   * @second useFetch error handler
   * @third useFetch success handler
   * @fourth npi lookup api trigger
   */
  const {
    data: npiLookupData,
    error: npiLookupError,
    mutate: lookupNpi,
  } = useFetch<NPILookupResponse>(APIEndpoints.NPI_LOOKUP);

  const clearFields = () => {
    reset();
    setValue("npi", preservedNpi);
    setValue("enrollment_practices_attributes.0.npi", preservedNpi);
    setValue("enrollment_doctors_attributes.0.npi", preservedNpi);
    updatePreservedNpi("");
    showToast([`No Data Found`], ToastTypes.ERROR);
  };

  const populateFields = (npiLookupData: NPILookupResponse) => {
    if (npiLookupData.practice_data)
      showToast([`Practice Data Found`], ToastTypes.INFO);
    if (npiLookupData.doctor_data)
      showToast([`Doctor Data Found`], ToastTypes.INFO);

    populateEnrollmentForm(setValue, npiLookupData, getValues("npi") || "");

    trigger([
      "enrollment_practices_attributes",
      "enrollment_doctors_attributes",
    ]);
  };

  const clearSpecificFields = (
    fields: string[],
    prefix: string,
    npiValue: string = "",
    errorMessage: string,
  ) => {
    fields.forEach((field) => setValue(`${prefix}.${field}` as FieldName, ""));
    setValue(`${prefix}.npi` as FieldName, npiValue);
    updatePreservedNpi("");
    showToast([errorMessage], ToastTypes.ERROR);
  };

  const clearPracticeField = () => {
    clearSpecificFields(
      [
        "practice_name",
        "phone_number",
        "street",
        "city",
        "zip",
        "tax_id",
        "fax_number",
        "suite",
        "state",
      ],
      "enrollment_practices_attributes.0",
      preservedNpi,
      "Practice Data Not Found",
    );
  };
  const clearDoctorField = () => {
    clearSpecificFields(
      [
        "firstname",
        "middlename",
        "lastname",
        "phone_number",
        "sln",
        "ptan",
        "tax_id",
        "ref_taxonomy_id",
        "fax_number",
        "dea",
        "email",
      ],
      "enrollment_doctors_attributes.0",
      preservedNpi,
      "Doctors Data Not Found",
    );
  };

  useEffect(() => {
    if (npiLookupData || npiLookupError) {
      hideLoader();
      if (npiLookupData) {
        const { doctor_data, practice_data } = npiLookupData;

        if (doctor_data !== null && practice_data !== null) {
          populateFields(npiLookupData);
        }
        if (doctor_data === null && practice_data === null) {
          clearFields();
        }
        if (doctor_data !== null && practice_data === null) {
          clearPracticeField();
        }
        if (doctor_data === null && practice_data !== null) {
          clearDoctorField();
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npiLookupData, npiLookupError]);

  const callNPILookup = () => {
    const npi = getValues("npi");

    if (!npiLookupData || npi !== preservedNpi) {
      if (!npi || (npi && npi.length === 0) || formState.errors.npi)
        showToast([`Please enter valid NPI for lookup`], ToastTypes.ERROR);
      else {
        setLoader();
        updatePreservedNpi(npi);
        const data = {
          "global_doctor[npi]": npi,
        };
        lookupNpi(null, null, data);
      }
    }
  };

  /**
   * @watchingChangesInFormElements
   * Adding new users/providers without mutating existing arrays
   */

  const userListing = watch("enrollment_contacts_attributes") || [];
  const prescriberListing = watch("enrollment_doctors_attributes") || [];

  const addNewUser = () => {
    const users = getValues("enrollment_contacts_attributes") || [];

    setValue(
      "enrollment_contacts_attributes",
      [...users, {} as EnrollmentUser],
      {
        shouldDirty: true,
        // shouldValidate: true,
        shouldTouch: true,
      },
    );
  };

  const removeUser = (userIndex: number) => {
    const users = getValues("enrollment_contacts_attributes") || [];
  
    setValue(
      "enrollment_contacts_attributes",
      users.filter((_u, index) => index !== userIndex),
      {
        shouldDirty: true,
        shouldValidate: false,
      },
    );
  };

  const addNewPrescriber = () => {
    const prescribers = getValues("enrollment_doctors_attributes") || [];

    setValue(
      "enrollment_doctors_attributes",
      [...prescribers, {} as EnrollmentPrescriber],
      {
        shouldDirty: true,
        // shouldValidate: true,
        shouldTouch: true,
      },
    );
  };

  const removePrescriber = (index: number) => {
    const prescribers = getValues("enrollment_doctors_attributes") || [];

    setValue(
      "enrollment_doctors_attributes",
      prescribers.filter((_p, i) => i !== index),
      {
        shouldDirty: true,
        // shouldValidate: true,
      },
    );
  };

  /**
   * @AddingNewEnrollment Sending entered validated data to API
   * @first adding useFetch to call api
   * @second useFetch error handler
   * @third useFetch success handler
   * @fourth submit form button action
   */
  const {
    loading: enrollmentLoading,
    error: enrollmentError,
    data: enrollmentData,
    mutate: addEnrollment,
    removeErrorAndData,
  } = useFetch<AddEnrollmentResponseType>(APIEndpoints.ADD_ENROLLMENT);

  useEffect(() => {
    if (enrollmentError) {
      const { message } = enrollmentError as unknown as AddEnrollmentErrorType;
      showToast([message], ToastTypes.ERROR);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollmentError]);

  useEffect(() => {
    if (enrollmentData) {
      const {
        status: { code, message },
      } = enrollmentData;
      if (code === 200) {
        showToast([message], ToastTypes.SUCCESS);
        setIsModalVisible(true);
        reset();
      } else {
        showToast([message], ToastTypes.ERROR);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollmentData]);

  const submitHandler = async (values: NewEnrollmentType) => {
    if (enrollmentError || enrollmentData) removeErrorAndData();

    const data: NewEnrollmentTypeForAPI =
      values as unknown as NewEnrollmentTypeForAPI;
    data.enrollment_practices_attributes[0].program =
      process.env.CLIENT_DB || "";
    // Grab only the file from the form
    const signatureFile =
      values.enrollment_doctors_attributes?.[0]?.signature_data;

    if (signatureFile instanceof File) {
      formData.append("signature_data", signatureFile);
    }
    console.log("fileeee", signatureFile);
    const doctorAttributes = values.enrollment_doctors_attributes.map((doc) => {
      return {
        ...doc,
        ref_taxonomy_id: (
          specialityData?.find(
            (speciality) => speciality.code === doc.ref_taxonomy_id,
          )?.id || 0
        ).toString(),
        signature_data: signatureFile,
      };
    });

    addEnrollment({
      enrollment: {
        ...data,
        enrollment_doctors_attributes: doctorAttributes,
      },
    });
  };

  return (
    <div className="container-fluid  pt-4 p-0 flex-fill overflow-auto enroll-page">
      <div className="container">
        <form onSubmit={handleSubmit(submitHandler)}>
          <EnrollmentCard
            isCrossBtnVisible={false}
            isTextCenter={true}
            cardHeader="MannkindCares Enrollment"
          >
            <p className="text-black text-center">
              Welcome to the mannkindCares enrollment page. To submit an
              enrollment request, please fill in as much detail as possible
              about your practice and doctors. All fields marked with an
              asterisk (*) below are required. All information provided will be
              stored in your browser session and will be automatically deleted
              when your session expires ( after there has been no action in at
              least 15 minutes). mannkindCares uses the information you submit
              on this form to process your enrollment request. By clicking on
              the "Submit Enrollment" button below, you agree that the
              information you provide will be governed by our site Privacy
              Policy.
            </p>
          </EnrollmentCard>

          <EnrollmentCard
            isTextCenter={false}
            isCrossBtnVisible={false}
            cardHeader="NPI"
          >
            <>
              <p className="text-start">
                Input a provider or practice NPI and we'll see if we can find
                the rest!
              </p>
              <Controller
                name="npi"
                control={control}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                  <NPIInputField
                    inputId="npi"
                    inputLabel="NPI"
                    isRequired={false}
                    isRowBased={true}
                    inputPlaceholder="NPI should be 10 Digits"
                    field={field}
                    error={error}
                    onBlur={callNPILookup}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        callNPILookup();
                      }
                    }}
                  />
                )}
              />
            </>
          </EnrollmentCard>

          <EnrollmentCard
            isTextCenter={false}
            isCrossBtnVisible={false}
            cardHeader="Practice"
          >
            <EnrollmentPracticeForm
              parentNpi={preservedNpi}
              control={control}
              formState={formState}
              getValues={getValues}
              setValue={setValue}
              resetField={resetField}
              trigger={trigger}
            />
          </EnrollmentCard>

          {userListing.map((_d, index) => (
            <EnrollmentUserForm
              key={`user-${index}`}
              removeUser={removeUser}
              control={control}
              userIndex={index}
            />
          ))}
          <div className="row justify-content-end mb-4">
            <div className="col-lg-3 text-end">
              <button
                className="btn btn-success"
                type="button"
                onClick={addNewUser}
              >
                + Add User
              </button>
            </div>
          </div>

          {prescriberListing.map((_d, index) => (
            <EnrollmentPrescriberForm
              key={`prescriber-${index}`}
              parentNpi={preservedNpi}
              control={control}
              prescriberIndex={index}
              formState={formState}
              getValues={getValues}
              setValue={setValue}
              trigger={trigger}
              specialityData={specialityData}
              resetField={resetField}
              removePrescriber={removePrescriber}
              watch={watch}
            />
          ))}
          <div className="row justify-content-end mb-2">
            <div className="col-lg-3 text-end">
              <button
                className="btn btn-success"
                type="button"
                onClick={addNewPrescriber}
              >
                + Add Prescriber
              </button>
            </div>
          </div>

          <div className="row mt-4 mb-5 mb-md-4">
            <div className="col-lg-12 text-center ">
              <button
                type="submit"
                disabled={enrollmentLoading}
                className="btn btn-primary btn-lg"
              >
                <div className="row align-items-center px-1">
                  {enrollmentLoading ? (
                    <div
                      className="spinner-border text-light col-3"
                      role="status"
                    >
                      <span className="sr-only"></span>
                    </div>
                  ) : null}
                  <p className="col-auto mb-0"> Submit Enrollment </p>
                </div>
              </button>
            </div>
          </div>
        </form>

        {
          <div
            className={`modal fade ${isModalVisible ? "show show-modal" : ""}`}
            id="exampleModal"
            tabIndex={-1}
            data-bs-backdrop="true"
            aria-hidden="true"
            //onClick={}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "600px" }}
            >
              <div className="modal-content">
                <div
                  className="modal-header align-items-center justify-content-between px-4"
                  style={{ borderBottom: "3px solid #BD1A8D" }}
                >
                  <h1
                    className="modal-title text-primary"
                    id="exampleModalLabel"
                  >
                    Registration Received
                  </h1>
                </div>

               <div className="modal-body p-4 px-4">
                  <p className="mb-4">
                    Thank you for registering for the MannKind Cares Healthcare
                    Provider Portal. Your registration requires authentication
                    prior to your account becoming active. You will receive an
                    email{enrollmentData?.status?.email ? ` at ${enrollmentData.status.email}` : ""} or phone call within the next 24 hours with
                    instructions on how to proceed.
                  </p>
                  <div className="text-end">
                    <button
                      className="btn btn-primary-dark px-4"
                      onClick={() => setIsModalVisible(false)}
                    >
                      Ok
                    </button>
                  </div>
                  </div>
              </div>
            </div>
          </div>
        }
        <FooterText footerType="black" />
      </div>
    </div>
  );
};

export default NewEnrollment;
